import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

export interface DropdownItem {
  description: string;
  mainText: string;
  placeId: string;
  secondaryText: string;
}

interface Location {
  lat: number;
  lng: number;
}

export interface TimeZone {
  timeZoneId: string;
  timeZoneName: string;
}

export interface AutoCompleteCallback {
  setBusinessSelectedDetailsCallback(
    place_id: string,
    place: google.maps.places.PlaceResult | null,
    timeZone: TimeZone,
  ): void;
}

@Injectable({ providedIn: 'root' })
export class AutocompleteService {
  private static NO_BUSINESS_FOUND = '___pcc_no_business_found';
  map: google.maps.Map;
  delegate: AutoCompleteCallback;
  private autocompleteSessionToken?: google.maps.places.AutocompleteSessionToken;
  private currentPosition?: Location;

  constructor(public http: HttpClient) {}

  private static parseAutoCompleteResponse(
    responses: google.maps.places.AutocompletePrediction[] | null,
  ): DropdownItem[] {
    const choices = [];
    if (responses) {
      for (let i = 0; i <= responses.length; i++) {
        if (responses[i]) {
          const drop_down: DropdownItem = AutocompleteService.convertToDropDown(responses[i]);
          choices.push(drop_down);
        }
      }
    }

    return choices;
  }

  private static convertToDropDown(response: google.maps.places.AutocompletePrediction): DropdownItem {
    return {
      mainText: response.structured_formatting.main_text,
      description: response.description,
      secondaryText: response.structured_formatting.secondary_text,
      placeId: response.place_id,
    };
  }

  public init(googleMap: google.maps.Map, delegate: AutoCompleteCallback): void {
    this.map = googleMap;
    this.delegate = delegate;

    this.http
      .post('https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyDxrR1bk6d2TRa4orIDF05H3UlhvIXRRC0', {})
      .pipe(
        tap((response: { location: Location }) => {
          if (response?.location) {
            this.currentPosition = response.location;
          }
        }),
        take(1),
      )
      .subscribe();

    this.autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  onSelect(placeId: string): void {
    if (placeId && placeId !== AutocompleteService.NO_BUSINESS_FOUND) {
      const placeService = new google.maps.places.PlacesService(this.map);
      const callback = (
        place: google.maps.places.PlaceResult | null,
        rs: google.maps.places.PlacesServiceStatus,
      ): void => {
        if (rs != google.maps.places.PlacesServiceStatus.OK) {
          console.log('Google Maps Service Status: ', rs);
        }
        this.getTimeZone(place).subscribe((timeZone) => {
          this.delegate.setBusinessSelectedDetailsCallback(placeId, place, timeZone);
        });
      };
      placeService.getDetails(
        {
          placeId: placeId,
          fields: ['formatted_phone_number', 'name', 'address_component', 'website', 'types', 'geometry'],
          sessionToken: this.autocompleteSessionToken,
        },
        callback,
      );
    }
  }

  // https://developers.google.com/maps/documentation/javascript/places-migration-autocomplete
  async getChoices(search_term: DropdownItem | string): Promise<DropdownItem[]> {
    if (!search_term) {
      return [];
    }

    let normalizedSearchTerm = '';

    if (typeof search_term != 'string') {
      normalizedSearchTerm = (search_term as { mainText: string })?.mainText ?? '';
    } else {
      normalizedSearchTerm = search_term;
    }

    normalizedSearchTerm = normalizedSearchTerm.toLowerCase();

    const request = {
      input: normalizedSearchTerm,
      includedPrimaryTypes: ['establishment'],
      // includePureServiceAreaBusinesses: true, // Not implemented yet in v 3.58 wait until 3.59
      sessionToken: this.autocompleteSessionToken,
    } as google.maps.places.AutocompleteRequest;
    if (this.currentPosition) {
      request.locationBias = {
        north: this.currentPosition.lat + 0.5,
        east: this.currentPosition.lng + 0.5,
        south: this.currentPosition.lat - 0.5,
        west: this.currentPosition.lng - 0.5,
      } as google.maps.LatLngBoundsLiteral;
    }

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    const choices: DropdownItem[] = [];
    if (suggestions) {
      for (const suggestion of suggestions) {
        const drop_down: DropdownItem = {
          mainText: suggestion.placePrediction?.mainText?.text || '',
          description: '',
          secondaryText: suggestion.placePrediction?.secondaryText?.text || '',
          placeId: suggestion.placePrediction?.placeId || '',
        };
        choices.push(drop_down);
      }
    }

    return choices;
  }

  getTimeZone(place: google.maps.places.PlaceResult | null): Observable<TimeZone> {
    const timeZoneApiUrl = 'https://maps.googleapis.com/maps/api/timezone/json';
    const paramKey = 'AIzaSyDxrR1bk6d2TRa4orIDF05H3UlhvIXRRC0';
    const paramLat = place?.geometry?.location?.lat();
    const paramLng = place?.geometry?.location?.lng();

    const params = {
      location: `${paramLat},${paramLng}`,
      timestamp: Math.floor(Date.now() / 1000).toString(),
      key: paramKey,
    };

    return this.http.get(timeZoneApiUrl, { params }).pipe(map((response) => response as TimeZone));
  }
}
